import { useState, useEffect, useCallback, useRef } from "react";
import { FileText, Image as ImageIcon, Inbox, PanelsTopLeft, Plus, Trash2, Upload, Copy, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail, API } from "../lib/api";
import { toast } from "sonner";
import { timeAgo, clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import SiteLayoutEditor from "../components/SiteLayoutEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const SECTION_TABS = [
  { id: "layout", label: "Header & Footer", icon: PanelsTopLeft },
  { id: "pages", label: "Pages", icon: FileText },
  { id: "media", label: "Media Library", icon: ImageIcon },
  { id: "contact", label: "Contact Inbox", icon: Inbox },
];

export default function AdminCMS() {
  const [sec, setSec] = useState("pages");
  const [pages, setPages] = useState([]);
  const [media, setMedia] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [pageForm, setPageForm] = useState(null);
  const fileRef = useRef(null);

  const loadPages = useCallback(() => api.get("/admin/pages").then((r) => setPages(r.data)), []);
  const loadMedia = useCallback(() => api.get("/admin/media").then((r) => setMedia(r.data)), []);
  const loadSubs = useCallback(() => api.get("/admin/contact-submissions").then((r) => setSubmissions(r.data)), []);

  useEffect(() => { loadPages(); loadMedia(); loadSubs(); }, [loadPages, loadMedia, loadSubs]);

  const newPage = () => setPageForm({ slug: "", title: "", status: "draft", show_in_nav: false, order: pages.length + 1, seo_title: "", seo_description: "", featured_image: "", hero_title: "", hero_subtitle: "", blocks: [{ heading: "", body: "" }] });
  const editPage = (p) => setPageForm({ ...p, blocks: p.blocks?.length ? p.blocks : [{ heading: "", body: "" }] });
  const setBlock = (i, k, v) => setPageForm((f) => ({ ...f, blocks: f.blocks.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const addBlock = () => setPageForm((f) => ({ ...f, blocks: [...f.blocks, { heading: "", body: "" }] }));
  const removeBlock = (i) => setPageForm((f) => ({ ...f, blocks: f.blocks.filter((_, idx) => idx !== i) }));

  const savePage = async () => {
    try {
      if (!pageForm.title || !pageForm.slug) { toast.error("Title and slug required"); return; }
      if (pageForm.id) await api.put(`/admin/pages/${pageForm.id}`, pageForm);
      else await api.post("/admin/pages", pageForm);
      toast.success("Page saved"); setPageForm(null); loadPages();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const deletePage = async (id) => { if (!window.confirm("Delete this page?")) return; await api.delete(`/admin/pages/${id}`); toast.success("Page deleted"); loadPages(); };

  const uploadMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "general");
    try {
      await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); loadMedia();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    if (fileRef.current) fileRef.current.value = "";
  };
  const deleteMedia = async (id) => { await api.delete(`/admin/media/${id}`); toast.success("Deleted"); loadMedia(); };
  const copyUrl = (m) => { const full = `${API}${m.url}`; navigator.clipboard?.writeText(full); toast.success("URL copied"); };

  const markRead = async (id) => { await api.post(`/admin/contact-submissions/${id}/read`); loadSubs(); };
  const deleteSub = async (id) => { await api.delete(`/admin/contact-submissions/${id}`); loadSubs(); };

  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        {SECTION_TABS.map((t) => (
          <button key={t.id} onClick={() => setSec(t.id)} data-testid={`cms-sec-${t.id}`}
            className={clsx("flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors",
              sec === t.id ? "bg-brandblue text-white border-brandblue" : "bg-fx-s1 text-fx-s3 border-white/5 hover:text-white")}>
            <t.icon className="h-4 w-4" /> {t.label}
            {t.id === "contact" && submissions.filter((s) => s.status === "new").length > 0 && (
              <span className="ml-1 h-4 min-w-4 px-1 rounded-full bg-neg text-white text-[9px] grid place-items-center">{submissions.filter((s) => s.status === "new").length}</span>
            )}
          </button>
        ))}
      </div>

      {/* PAGES */}
      {sec === "layout" && <SiteLayoutEditor />}

      {sec === "pages" && (
        <div className="space-y-4">
          <div className="flex justify-end"><Button onClick={newPage} data-testid="cms-new-page" className="bg-brandblue hover:bg-blue-500 gap-2"><Plus className="h-4 w-4" /> New Page</Button></div>
          <div className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-fx-s3 border-b border-white/5">
                <th className="px-4 py-3">Title</th><th className="px-4 py-3">Slug</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">In Nav</th><th className="px-4 py-3">Updated</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {pages.map((p) => (
                  <tr key={p.id} data-testid={`cms-page-${p.slug}`} className="border-b border-white/[0.03]">
                    <td className="px-4 py-3 font-medium">{p.title}</td>
                    <td className="px-4 py-3 font-mono text-xs text-fx-s3">/{p.slug}</td>
                    <td className="px-4 py-3"><span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono", p.status === "published" ? "text-pos bg-pos/15" : "text-fx-s3 bg-white/5")}>{p.status}</span></td>
                    <td className="px-4 py-3">{p.show_in_nav ? <Eye className="h-4 w-4 text-pos" /> : <EyeOff className="h-4 w-4 text-fx-s3" />}</td>
                    <td className="px-4 py-3 text-xs text-fx-s3">{timeAgo(p.updated_at)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button onClick={() => editPage(p)} data-testid={`cms-edit-${p.slug}`} size="sm" className="bg-brandblue hover:bg-blue-500 h-7 mr-2">Edit</Button>
                      <Button onClick={() => deletePage(p.id)} variant="ghost" size="sm" className="text-neg h-7">Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MEDIA */}
      {sec === "media" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadMedia} className="hidden" data-testid="cms-media-input" />
            <Button onClick={() => fileRef.current?.click()} data-testid="cms-upload-media" className="bg-brandblue hover:bg-blue-500 gap-2"><Upload className="h-4 w-4" /> Upload Image</Button>
          </div>
          {media.length === 0 && <p className="text-fx-s3 text-sm">No media yet. Upload images to reuse them across the site.</p>}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {media.map((m) => (
              <div key={m.id} data-testid={`cms-media-${m.id}`} className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden group">
                <div className="aspect-square bg-fx-base grid place-items-center overflow-hidden">
                  <img src={`${API}${m.url}`} alt={m.filename} className="w-full h-full object-cover" />
                </div>
                <div className="p-2">
                  <div className="text-[11px] text-fx-s3 truncate">{m.filename}</div>
                  <div className="flex gap-1 mt-1">
                    <Button onClick={() => copyUrl(m)} size="sm" variant="ghost" className="h-6 px-2 text-brandblue gap-1"><Copy className="h-3 w-3" /> URL</Button>
                    <Button onClick={() => deleteMedia(m.id)} size="sm" variant="ghost" data-testid={`cms-media-delete-${m.id}`} className="h-6 px-2 text-neg gap-1"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CONTACT INBOX */}
      {sec === "contact" && (
        <div className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-fx-s3 border-b border-white/5">
              <th className="px-4 py-3">From</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Message</th><th className="px-4 py-3">When</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {submissions.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-fx-s3">No messages yet.</td></tr>}
              {submissions.map((s) => (
                <tr key={s.id} data-testid={`cms-sub-${s.id}`} className={clsx("border-b border-white/[0.03]", s.status === "new" && "bg-brandblue/[0.05]")}>
                  <td className="px-4 py-3"><div className="font-medium">{s.name}</div><div className="text-xs text-fx-s3 font-mono">{s.email}</div>{s.phone && <div className="text-[11px] text-fx-s3">{s.phone}</div>}</td>
                  <td className="px-4 py-3">{s.subject}{s.status === "new" && <span className="ml-2 text-[10px] text-brandblue">NEW</span>}</td>
                  <td className="px-4 py-3 text-fx-s3 max-w-[320px]"><div className="line-clamp-2">{s.message}</div></td>
                  <td className="px-4 py-3 text-xs text-fx-s3">{timeAgo(s.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {s.status === "new" && <Button onClick={() => markRead(s.id)} size="sm" className="bg-brandblue hover:bg-blue-500 h-7 mr-2" data-testid={`cms-sub-read-${s.id}`}>Mark read</Button>}
                    <Button onClick={() => deleteSub(s.id)} variant="ghost" size="sm" className="text-neg h-7">Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Page editor dialog */}
      <Dialog open={!!pageForm} onOpenChange={(o) => !o && setPageForm(null)}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cms-page-dialog">
          <DialogHeader><DialogTitle className="font-display">{pageForm?.id ? "Edit Page" : "New Page"}</DialogTitle></DialogHeader>
          {pageForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Title</Label><Input data-testid="cms-page-title" value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} className="bg-fx-base border-white/10" /></div>
                <div className="space-y-1"><Label className="text-xs">Slug (URL)</Label><Input data-testid="cms-page-slug" value={pageForm.slug} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value })} className="bg-fx-base border-white/10 font-mono text-xs" placeholder="my-page" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Hero Title</Label><Input value={pageForm.hero_title} onChange={(e) => setPageForm({ ...pageForm, hero_title: e.target.value })} className="bg-fx-base border-white/10" /></div>
              <div className="space-y-1"><Label className="text-xs">Hero Subtitle</Label><Textarea value={pageForm.hero_subtitle} onChange={(e) => setPageForm({ ...pageForm, hero_subtitle: e.target.value })} className="bg-fx-base border-white/10" /></div>
              <div className="space-y-1"><Label className="text-xs">Featured Image URL</Label><Input value={pageForm.featured_image} onChange={(e) => setPageForm({ ...pageForm, featured_image: e.target.value })} className="bg-fx-base border-white/10 font-mono text-xs" /></div>

              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between"><Label className="text-xs">Content Blocks</Label><Button onClick={addBlock} size="sm" variant="outline" className="h-7 border-white/10 bg-fx-base gap-1" data-testid="cms-add-block"><Plus className="h-3 w-3" /> Add block</Button></div>
                {pageForm.blocks.map((b, i) => (
                  <div key={i} className="rounded-lg bg-fx-base border border-white/5 p-3 space-y-2">
                    <div className="flex gap-2">
                      <Input value={b.heading} onChange={(e) => setBlock(i, "heading", e.target.value)} placeholder="Heading" className="bg-fx-s1 border-white/10" data-testid={`cms-block-heading-${i}`} />
                      <Button onClick={() => removeBlock(i)} size="sm" variant="ghost" className="text-neg h-9 px-2"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <Textarea value={b.body} onChange={(e) => setBlock(i, "body", e.target.value)} placeholder="Body text" className="bg-fx-s1 border-white/10" data-testid={`cms-block-body-${i}`} />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="space-y-1"><Label className="text-xs">SEO Title</Label><Input value={pageForm.seo_title} onChange={(e) => setPageForm({ ...pageForm, seo_title: e.target.value })} className="bg-fx-base border-white/10" /></div>
                <div className="space-y-1"><Label className="text-xs">Order</Label><Input type="number" value={pageForm.order} onChange={(e) => setPageForm({ ...pageForm, order: Number(e.target.value) })} className="bg-fx-base border-white/10 font-mono" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">SEO Description</Label><Textarea value={pageForm.seo_description} onChange={(e) => setPageForm({ ...pageForm, seo_description: e.target.value })} className="bg-fx-base border-white/10" /></div>
              <div className="flex items-center gap-6 pt-1">
                <div className="flex items-center gap-2"><Switch checked={pageForm.status === "published"} onCheckedChange={(v) => setPageForm({ ...pageForm, status: v ? "published" : "draft" })} data-testid="cms-page-published" /><Label className="text-xs">Published</Label></div>
                <div className="flex items-center gap-2"><Switch checked={pageForm.show_in_nav} onCheckedChange={(v) => setPageForm({ ...pageForm, show_in_nav: v })} data-testid="cms-page-nav" /><Label className="text-xs">Show in footer nav</Label></div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={savePage} data-testid="cms-page-save" className="bg-brandblue hover:bg-blue-500">Save Page</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
